"use client";
import { createLoginSession } from "@/api/login-session";
import { getUnreadNotificationCountByUserId } from "@/api/notification";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { enqueueSnackbar } from "notistack";
import { createContext, useEffect, useState, useReducer } from "react";
import { IP_INFO_API_KEY } from "../../../config";
import { getTotalUnreadMessageCount } from "@/api/chat-rooms";
import { getCurrentAuthUser } from "@/api/auth";

const initialState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  subscriptionPlan: null,
  isOnBordingCompleted: false,
  forgatPasswordType: "",
  socket: null,
  totalUnreadNotifications: 0,
  totalUnreadMessageCount: 0,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      ...state,
      user: action.payload.user,
      isAuthenticated: action.payload.isAuthenticated,
      isLoading: false,
    };
  }
  if (action.type === "LOGIN") {
    const { user } = action.payload || {};
    return {
      ...state,
      isAuthenticated: true,
      user: user,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      isAuthenticated: false,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === "CHANGE_LOADING") {
    return {
      ...state,
      isLoading: false,
    };
  }
  if (action.type === "FORGET_PASSWORD_TYPE") {
    return {
      ...state,
      forgatPasswordType: action.payload.forgatPasswordType,
    };
  }
  if (action.type === "UPDATE_SOCKET") {
    return {
      ...state,
      socket: action.payload.socket,
    };
  }
  if (action.type === "UPDATE_UNREAD_NOTIFS") {
    return {
      ...state,
      totalUnreadNotifications: action.payload,
    };
  }
  if (action.type === "UPDATE_UNREAD_MESSAGES") {
    return {
      ...state,
      totalUnreadMessageCount: action.payload,
    };
  }
  if (action.type === "INC_UNREAD_MESSAGES") {
    return {
      ...state,
      totalUnreadMessageCount: state.totalUnreadMessageCount + action.payload,
    };
  }
  if (action.type === "DEC_UNREAD_MESSAGES") {
    if (state.totalUnreadMessageCount - action.payload >= 0) {
      return {
        ...state,
        totalUnreadMessageCount: state.totalUnreadMessageCount - action.payload,
      };
    } else {
      return {
        ...state,
        totalUnreadMessageCount: 0,
      };
    }
  }
  return state;
};

export const AuthContext = createContext(null);

const getBrowserInfo = (userAgent = "") => {
  const browserPatterns = [
    ["Edge", /Edg\/([\d.]+)/],
    ["Opera", /OPR\/([\d.]+)/],
    ["Samsung Internet", /SamsungBrowser\/([\d.]+)/],
    ["Chrome", /Chrome\/([\d.]+)/],
    ["Firefox", /Firefox\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];

  for (const [name, pattern] of browserPatterns) {
    const match = userAgent.match(pattern);
    if (match) return { name, version: match[1] };
  }

  return { name: "Unknown", version: "" };
};

const getOSInfo = (userAgent = "") => {
  const osPatterns = [
    ["Windows", /Windows NT ([\d.]+)/],
    ["Android", /Android ([\d.]+)/],
    ["iOS", /(?:iPhone|iPad|iPod).*OS ([\d_]+)/],
    ["macOS", /Mac OS X ([\d_]+)/],
    ["Linux", /Linux/],
  ];

  for (const [name, pattern] of osPatterns) {
    const match = userAgent.match(pattern);
    if (match) {
      return {
        name,
        version: match[1] ? match[1].replaceAll("_", ".") : "",
      };
    }
  }

  return { name: "Unknown", version: "" };
};

const syncAuthCookies = (user) => {
  if (!user?.userId || !user?.role || !user?.exp) return;

  const cookieOptions = {
    expires: new Date(user.exp * 1000),
    secure: process.env.NODE_ENV === "production",
    sameSite: "strict",
    path: "/",
  };

  Cookies.set("userId", user.userId, cookieOptions);
  Cookies.set("role", user.role, cookieOptions);
};

const normalizeAuthUser = (decodedUser, currentUser) => {
  if (!currentUser?.id && !currentUser?.userId) return decodedUser;

  return {
    ...decodedUser,
    ...currentUser,
    userId: currentUser.userId || currentUser.id || decodedUser?.userId,
    role: currentUser.role || decodedUser?.role,
    username: currentUser.username || decodedUser?.username,
    name: currentUser.name || decodedUser?.name,
    profilePicture:
      currentUser.profilePicture ?? decodedUser?.profilePicture ?? null,
    exp: decodedUser?.exp,
    iat: decodedUser?.iat,
  };
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading] = useState(false);
  const [userIpInfo, setUserIpInfo] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModelOpen, setIsRegisterModelOpen] = useState(false);
  const [pendingDisplayNameUser, setPendingDisplayNameUser] = useState(null);
  // const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(() => {
  //   // Load from localStorage on initialization
  //   const saved = localStorage.getItem('livePhotoAutoplay');
  //   return saved !== null ? JSON.parse(saved) : true; // Default to true
  // });

  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(true);

  const getBrowserAndOSInfo = () => {
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const browser = getBrowserInfo(userAgent);
    const os = getOSInfo(userAgent);

    return {
      browserName: browser.name,
      browserVersion: browser.version,
      osName: os.name,
      osVersion: os.version,
    };
  };

  const handleLoginSession = async (userId, status, isLogout = false) => {
    try {
      if (!userId) return;

      const info = getBrowserAndOSInfo();

      const ipResponse = await fetch(
        `https://ipinfo.io/?token=${IP_INFO_API_KEY}`
      );
      const ipData = await ipResponse.json();

      setUserIpInfo(ipData);

      const payload = {
        userId,
        status,
        browser: `${info?.browserName} ${info?.browserVersion}`,
        os: `${info?.osName} ${info?.osVersion}`,
        ip: ipData?.ip,
        ipLocation: `${ipData?.org} ${ipData?.city}, ${ipData?.postal}, ${ipData?.region}, ${ipData?.country}`,
      };

      if (isLogout) {
        payload.logoutTime = new Date();
      } else {
        payload.loginTime = new Date();
      }

      const response = await createLoginSession(payload);
      if (response?.error) {
        enqueueSnackbar(response?.error || "Error", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || "Error",
        { variant: "error" }
      );
    }
  };

  const openLoginModal = () => setIsLoginModalOpen(true);

  useEffect(() => {
    let isCancelled = false;

    const initializeAuth = async () => {
      if (isCancelled) return;

      const token = localStorage.getItem("jwtToken");
      if (token) {
        try {
          const decodedUser = jwtDecode(token);
          let user = decodedUser;
          syncAuthCookies(decodedUser);

          dispatch({
            type: "INITIAL",
            payload: {
              user: decodedUser,
              isAuthenticated: true,
            },
          });

          try {
            const currentAuth = await getCurrentAuthUser();
            if (isCancelled) return;

            if (currentAuth?.token) {
              localStorage.setItem("jwtToken", currentAuth.token);
              user = jwtDecode(currentAuth.token);
            } else if (currentAuth?.user) {
              user = normalizeAuthUser(decodedUser, currentAuth.user);
            }
          } catch {
            return;
          }

          syncAuthCookies(user);

          dispatch({
            type: "INITIAL",
            payload: {
              user,
              isAuthenticated: true,
            },
          });
        } catch {
          localStorage.removeItem("jwtToken");
          Cookies.remove("userId", { path: "/" });
          Cookies.remove("role", { path: "/" });
          if (isCancelled) return;
          dispatch({
            type: "INITIAL",
            payload: {
              user: null,
              isAuthenticated: false,
            },
          });
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            user: null,
            isAuthenticated: false,
          },
        });
      }
    };

    initializeAuth();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("livePhotoAutoplay");
    if (saved) {
      setIsAutoplayEnabled(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (state.socket && state.user) {
      // Example: listen for 'notification' events
      state.socket.on("notification", (data) => {
        fetchUserUnreadNotifsCount();
      });

      return () => {
        state.socket.off("notification"); // Clean up listener on component unmount
      };
    }
  }, [state.socket]);

  const fetchUserUnreadNotifsCount = async () => {
    if (state.user?.userId) {
      try {
        const response = await getUnreadNotificationCountByUserId(
          state.user?.userId
        );
        if (response?.error) {
          console.log(response);
          return;
        }
        if (response) {
          dispatch({
            type: "UPDATE_UNREAD_NOTIFS",
            payload: response.totalUnreadNotifs || 0,
          });
        }
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message || error?.message || "Error",
          { variant: "error" }
        );
      }
    }
  };

  const fetchUserUnreadMessageCount = async () => {
    if (state.user?.userId) {
      try {
        const response = await getTotalUnreadMessageCount({
          userId: state.user?.userId,
        });
        if (response?.error) {
          console.log(response);
          return;
        }

        dispatch({
          type: "UPDATE_UNREAD_MESSAGES",
          payload: response?.unread || 0,
        });
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message || error?.message || "Error",
          { variant: "error" }
        );
      }
    }
  };

  useEffect(() => {
    if (state.user?.userId) {
      fetchUserUnreadNotifsCount();
      fetchUserUnreadMessageCount();
    }
  }, [state.user]);

  useEffect(() => {
    localStorage.setItem(
      "livePhotoAutoplay",
      JSON.stringify(isAutoplayEnabled)
    );
  }, [isAutoplayEnabled]);

  const toggleAutoplay = () => {
    setIsAutoplayEnabled((prev) => !prev);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        dispatch,
        isRegisterModelOpen,
        setIsRegisterModelOpen,
        isLoginModalOpen,
        setIsLoginModalOpen,
        pendingDisplayNameUser,
        setPendingDisplayNameUser,
        fetchUserUnreadNotifsCount,
        handleLoginSession,
        openLoginModal,
        userIpInfo,
        isAutoplayEnabled,
        setIsAutoplayEnabled,
        toggleAutoplay,
      }}
    >
      {loading ? null : children}
    </AuthContext.Provider>
  );
}
